import { jsx as _jsx } from "react/jsx-runtime";
import { memo, useMemo } from 'react';
import { useChartData } from '../../context/ChartContext';
import { useActiveSpectrumPeaksViewState } from '../../hooks/useActiveSpectrumPeaksViewState';
import { useActiveSpectrumRangesViewState } from '../../hooks/useActiveSpectrumRangesViewState';
import { usePanelPreferences } from '../../hooks/usePanelPreferences';
import useSpectrum from '../../hooks/useSpectrum';
import { useScaleX } from '../utilities/scale';
import PeakAnnotations from './PeakAnnotations';
import PeakAnnotationsSpreadMode from './PeakAnnotationsSpreadMode';
export function getHighlightSource(peaksSource) {
    return peaksSource === 'peaks' ? 'PEAK' : 'RANGE_PEAK';
}
export function getHighlightExtraId(peaksSource, id, parentKeys) {
    return peaksSource === 'peaks' ? id : [...parentKeys, id].join(',');
}
function flatRangesPeaks(ranges) {
    const results = [];
    for (const { signals = [], id: rangeID } of ranges) {
        for (const { peaks = [], id: signalID } of signals) {
            for (const peak of peaks) {
                results.push({ ...peak, parentKeys: [rangeID, signalID] });
            }
        }
    }
    return results;
}
function mapPeaks(peaks, scale) {
    return peaks
        .map((peak) => ({
        ...peak,
        scaleX: scale(peak.x),
    }))
        .sort((p1, p2) => p2.x - p1.x);
}
function useMapPeaks(spectrum, filterBy) {
    const scaleX = useScaleX();
    return useMemo(() => {
        switch (filterBy) {
            case 'Source[peaks]_Mode[single]':
                return spectrum.peaks.values;
            case 'Source[peaks]_Mode[spread]':
                return mapPeaks(spectrum.peaks.values, (val) => scaleX()(val));
            case 'Source[ranges]_Mode[single]':
                return flatRangesPeaks(spectrum.ranges.values);
            case 'Source[ranges]_Mode[spread]':
                return mapPeaks(flatRangesPeaks(spectrum.ranges.values), (val) => scaleX()(val));
            default:
                return [];
        }
    }, [scaleX, spectrum, filterBy]);
}
function InnerPeaks(props) {
    const { peaksSource, spectrum, mode, displayerKey, xDomain, peakFormat } = props;
    const peaks = useMapPeaks(spectrum, `Source[${peaksSource}]_Mode[${mode}]`);
    if (mode === 'spread') {
        return (_jsx(PeakAnnotationsSpreadMode, { peaksSource: peaksSource, peaks: peaks, spectrumId: spectrum.id, spectrumColor: spectrum.display.color, peakFormat: peakFormat, displayerKey: displayerKey, xDomain: xDomain }));
    }
    return (_jsx(PeakAnnotations, { peaksSource: peaksSource, peaks: peaks, spectrumId: spectrum.id, spectrumColor: spectrum.display.color, peakFormat: peakFormat, displayerKey: displayerKey, xDomain: xDomain }));
}
const MemoizedPeaksPanel = memo(InnerPeaks);
const emptyData = { peaks: {}, ranges: {}, info: {}, display: {} };
export default function Peaks(props) {
    const { peaksSource } = props;
    const { view: { spectra: { activeTab: nucleus }, }, displayerKey, xDomain, } = useChartData();
    const spectrum = useSpectrum(emptyData);
    const peaksViewState = useActiveSpectrumPeaksViewState();
    const rangesViewState = useActiveSpectrumRangesViewState();
    const { deltaPPM: { format: peakFormat }, } = usePanelPreferences(peaksSource === 'peaks' ? 'peaks' : 'ranges', nucleus);
    const canDisplaySpectrumPeaks = !spectrum.display.isVisible || spectrum.info?.isFid;
    let mode = 'spread';
    if (peaksSource === 'peaks' &&
        (spectrum.peaks?.values?.length === 0 ||
            !peaksViewState.showPeaks ||
            canDisplaySpectrumPeaks)) {
        return null;
    }
    if (peaksSource === 'ranges' &&
        (spectrum.ranges?.values?.length === 0 ||
            !rangesViewState.showPeaks ||
            canDisplaySpectrumPeaks)) {
        return null;
    }
    if (peaksSource === 'peaks') {
        mode = peaksViewState?.displayingMode || 'spread';
    }
    if (peaksSource === 'ranges') {
        mode = rangesViewState?.displayingMode || 'spread';
    }
    return (_jsx(MemoizedPeaksPanel, { spectrum, mode, peaksSource, displayerKey, xDomain, peakFormat }));
}
